import * as swcHelpers from '@swc/helpers';
import { getAttrValue } from '@grandvisionhq/utils';
import { mapBundleProduct } from '../mappers/view/line-item/bundle-product.js';
import { getContactLensSpecs } from './get-contact-lens-specs.js';
import { getEyeSide } from './get-eye-side.js';
import { getAttribute } from './get-line-item-attribute.js';
import { getLineItemImageUrl } from './get-line-item-image-url.js';
import { getLocalizedAttribute } from './get-line-item-localized-attribute.js';
import { getLocalizedAttributes } from './get-line-item-localized-attributes.js';
import { getProductUrl } from './get-line-item-product-url.js';
import { getLocalizedString } from './get-localized-string.js';
import { getSharedProps } from './shared-line-item-data-mapper.js';

const mapRegularProduct = (lineItemRaw, settings, intl)=>{
    const product = lineItemRaw.legacyProduct;
    const variant = product.masterData.current.variant;
    const shared = getSharedProps(lineItemRaw, product, intl);
    const slug = getLocalizedString(intl.locale, product.masterData.current.slug);
    const imageUrl = getLineItemImageUrl(variant);
    const exclusive = getAttrValue(variant, 'exclusiveBrand');
    const brand = swcHelpers.objectSpread({
        name: getAttribute(variant, 'brand'),
        model: getAttribute(variant, 'brandModel'),
        modelCode: getAttribute(variant, 'brandModelCode'),
        modelColorCode: getAttribute(variant, 'brandModelColorCode')
    }, typeof exclusive === 'boolean' && {
        exclusive
    });
    const productAttributes = {
        sku: lineItemRaw.sku,
        productSlug: slug,
        productId: product.id,
        productUrl: getProductUrl(settings.productLinkFormatter, lineItemRaw, slug, intl.locale),
        brand,
        erpId: getAttribute(variant, 'erpId')
    };
    switch(product.productType.toLowerCase()){
        case 'frames':
            return swcHelpers.objectSpread({
                type: 'frames'
            }, shared, productAttributes, {
                imageUrl,
                subtitle: intl.getLabel('products.frames.frameOnly.description'),
                frame: {
                    // Only used for analytics so needs to be english
                    frameType: getLocalizedAttribute(variant, 'frameType', 'en'),
                    faceShape: getLocalizedAttribute(variant, 'faceShape', 'en')
                }
            });
        case 'solutions':
            return swcHelpers.objectSpread({
                type: 'solutions'
            }, shared, productAttributes, {
                imageUrl,
                solutionType: getAttribute(variant, 'solutionType'),
                solutionVolume: getAttribute(variant, 'volume'),
                isSubscription: lineItemRaw.typeDetails.isSubscription,
                subscriptionDetails: lineItemRaw.typeDetails.subscriptionDetails || undefined
            });
        case 'contact-lenses':
            return swcHelpers.objectSpread({
                type: 'contact-lenses'
            }, shared, productAttributes, {
                imageUrl,
                colorLens: getAttribute(variant, 'color'),
                eyeSide: getEyeSide(lineItemRaw.typeDetails),
                lensType: getLocalizedAttribute(variant, 'lensType', intl.locale) || '',
                material: getLocalizedAttribute(variant, 'material', intl.locale) || '',
                packageQuantity: getAttribute(variant, 'packageQuantity'),
                replacementFrequency: getLocalizedAttribute(variant, 'replacementFrequency', intl.locale),
                sapArticleNumber: getAttribute(variant, 'sapArticleNumber'),
                specs: getContactLensSpecs(variant.attributes),
                isSubscription: lineItemRaw.typeDetails.isSubscription,
                subscriptionDetails: lineItemRaw.typeDetails.subscriptionDetails || undefined
            });
        case 'sunglasses':
            return swcHelpers.objectSpread({
                type: 'sunglasses'
            }, shared, productAttributes, {
                imageUrl,
                imageAlt: `${brand.name} ${brand.model}`,
                subtitle: `${brand.model} ${brand.modelCode && brand.modelCode !== brand.model ? brand.modelCode : ''}`,
                frame: {
                    frameShape: getLocalizedAttribute(variant, 'frameShape', intl.locale),
                    frameMaterial: getLocalizedAttributes(variant, 'frameMaterial', intl.locale),
                    frameColor: getLocalizedAttributes(variant, 'frameColor', intl.locale),
                    // Only used for analytics so needs to be english
                    frameType: getLocalizedAttribute(variant, 'frameType', 'en'),
                    faceShape: getLocalizedAttribute(variant, 'faceShape', 'en')
                },
                gender: getLocalizedAttributes(variant, 'gender', intl.locale)
            });
        case 'ready-readers':
            return swcHelpers.objectSpread({
                type: 'ready-readers'
            }, shared, productAttributes, {
                gender: getLocalizedAttributes(variant, 'gender', intl.locale),
                imageUrl,
                power: getAttribute(variant, 'power'),
                frame: {
                    // Only used for analytics so needs to be english
                    frameType: getLocalizedAttribute(variant, 'frameType', 'en'),
                    faceShape: getLocalizedAttribute(variant, 'faceShape', 'en')
                }
            });
        case 'accessories':
            return swcHelpers.objectSpread({
                type: 'accessories'
            }, shared, productAttributes, {
                gender: getLocalizedAttributes(variant, 'gender', intl.locale),
                material: getLocalizedAttribute(variant, 'material', intl.locale) || '',
                accessoryType: getLocalizedAttribute(variant, 'accessoryType', intl.locale) || '',
                accessoryColor: getLocalizedAttributes(variant, 'accessoryColor', intl.locale),
                imageUrl
            });
        default:
            console.log(`Encountered unsupported productType '${product.productType}' with sku '${variant.sku}'`);
            return null;
    }
};
// Maps the server model to client view model
const lineItemDataMapper = (lineItemsRaw, settings, intl)=>{
    const lineItems = lineItemsRaw.map((lineItemRaw)=>{
        if (lineItemRaw.__typename === 'LineItemBundle') {
            return mapBundleProduct(lineItemRaw, settings, intl);
        }
        if (lineItemRaw.__typename === 'LineItemRegular' || lineItemRaw.__typename === 'LineItemContactLenses') {
            return mapRegularProduct(lineItemRaw, settings, intl);
        }
        return null;
    });
    return lineItems.filter((lineItem)=>Boolean(lineItem)
    );
};

export { lineItemDataMapper };
//# sourceMappingURL=line-item-data-mapper.js.map
